import {Token, tokenize} from "./lexer";
import {
    Assign,
    BlockStmt,
    ClassDecl,
    Expr,
    JavaType,
    MethodDecl,
    Param,
    Program,
    Stmt,
} from "./ast";

const SUPPORTED_IMPORTS = new Set(["java.util.Scanner"]);

export class ParseError extends Error {
    line: number;

    constructor(message: string, line: number) {
        super(message);
        this.line = line;
    }
}

const TYPE_KEYWORDS = new Set(["int", "long", "double", "float", "boolean", "char", "String", "void"]);
const MODIFIERS = new Set(["public", "private", "protected", "static", "final"]);

export function parse(source: string): Program {
    const tokens = tokenize(source);
    let pos = 0;
    let scannerImported = false;

    const peek = (offset = 0): Token => tokens[Math.min(pos + offset, tokens.length - 1)];
    const current = (): Token => peek();
    const atEnd = (): boolean => current().type === "EOF";

    const advance = (): Token => {
        const t = tokens[pos];
        if (pos < tokens.length - 1) pos++;
        return t;
    };

    const check = (type: Token["type"], value?: string): boolean => {
        const t = current();
        if (t.type !== type) return false;
        return value === undefined || t.value === value;
    };

    const match = (type: Token["type"], value?: string): boolean => {
        if (check(type, value)) {
            advance();
            return true;
        }
        return false;
    };

    const expect = (type: Token["type"], value?: string): Token => {
        if (check(type, value)) return advance();
        const t = current();
        throw new ParseError(
            `Expected ${value ?? type} but found '${t.value || t.type}'`,
            t.line
        );
    };

    const isTypeAhead = (): boolean => {
        const t = current();
        if (t.type === "KEYWORD" && TYPE_KEYWORDS.has(t.value)) return true;
        if (t.type === "IDENT" && t.value === "String") return true;
        if (t.type === "IDENT" && t.value === "Scanner" && scannerImported) return true;
        return false;
    };

    const parseType = (): JavaType => {
        const t = current();
        if (!isTypeAhead()) throw new ParseError(`Expected a type but found '${t.value}'`, t.line);
        advance();
        while (match("PUNCT", "[") ) {
            expect("PUNCT", "]");
        }
        return t.value as JavaType;
    };

    const skipModifiers = () => {
        while (current().type === "KEYWORD" && MODIFIERS.has(current().value)) advance();
    };

    function parseImport(): string {
        const line = current().line;
        expect("KEYWORD", "import");
        let path = expect("IDENT").value;
        while (match("PUNCT", ".")) {
            if (match("PUNCT", "*")) {
                path += ".*";
                break;
            }
            path += "." + expect("IDENT").value;
        }
        expect("PUNCT", ";");
        if (!SUPPORTED_IMPORTS.has(path)) {
            throw new ParseError(
                `Unsupported import '${path}'. The playground currently supports: ${Array.from(SUPPORTED_IMPORTS).join(", ")}`,
                line
            );
        }
        if (path === "java.util.Scanner") scannerImported = true;
        return path;
    }

    function parseProgram(): Program {
        const imports: string[] = [];
        while (check("KEYWORD", "import")) {
            imports.push(parseImport());
        }
        const classes: ClassDecl[] = [];
        while (!atEnd()) {
            classes.push(parseClass());
        }
        return {kind: "Program", imports, classes};
    }

    function parseClass(): ClassDecl {
        skipModifiers();
        const line = current().line;
        expect("KEYWORD", "class");
        const name = expect("IDENT").value;
        expect("PUNCT", "{");
        const methods: MethodDecl[] = [];
        while (!check("PUNCT", "}")) {
            methods.push(parseMethod());
        }
        expect("PUNCT", "}");
        return {kind: "Class", name, methods, line};
    }

    function parseMethod(): MethodDecl {
        skipModifiers();
        const line = current().line;
        const returnType = parseType();
        const name = expect("IDENT").value;
        expect("PUNCT", "(");
        const params: Param[] = [];
        if (!check("PUNCT", ")")) {
            do {
                const type = parseType();
                const pname = expect("IDENT").value;
                params.push({name: pname, type});
            } while (match("PUNCT", ","));
        }
        expect("PUNCT", ")");
        expect("PUNCT", "{");
        const body: Stmt[] = [];
        while (!check("PUNCT", "}")) {
            body.push(parseStatement());
        }
        expect("PUNCT", "}");
        return {kind: "Method", name, returnType, params, body, line};
    }

    function parseBlock(): Stmt[] {
        expect("PUNCT", "{");
        const stmts: Stmt[] = [];
        while (!check("PUNCT", "}")) {
            stmts.push(parseStatement());
        }
        expect("PUNCT", "}");
        return stmts;
    }

    function parseStatementOrBlockAsList(): Stmt[] {
        if (check("PUNCT", "{")) return parseBlock();
        return [parseStatement()];
    }

    function parseStatement(): Stmt {
        const t = current();

        if (t.type === "PUNCT" && t.value === "{") {
            const body = parseBlock();
            return {kind: "Block", body, line: t.line} as BlockStmt;
        }

        if (t.type === "KEYWORD" && t.value === "if") {
            advance();
            expect("PUNCT", "(");
            const cond = parseExpr();
            expect("PUNCT", ")");
            const then = parseStatementOrBlockAsList();
            let elseBranch: Stmt[] | null = null;
            if (match("KEYWORD", "else")) {
                if (check("KEYWORD", "if")) {
                    elseBranch = [parseStatement()];
                } else {
                    elseBranch = parseStatementOrBlockAsList();
                }
            }
            return {kind: "If", cond, then, elseBranch, line: t.line};
        }

        if (t.type === "KEYWORD" && t.value === "while") {
            advance();
            expect("PUNCT", "(");
            const cond = parseExpr();
            expect("PUNCT", ")");
            const body = parseStatementOrBlockAsList();
            return {kind: "While", cond, body, line: t.line};
        }

        if (t.type === "KEYWORD" && t.value === "do") {
            advance();
            const body = parseStatementOrBlockAsList();
            expect("KEYWORD", "while");
            expect("PUNCT", "(");
            const cond = parseExpr();
            expect("PUNCT", ")");
            expect("PUNCT", ";");
            return {kind: "DoWhile", cond, body, line: t.line};
        }

        if (t.type === "KEYWORD" && t.value === "for") {
            advance();
            expect("PUNCT", "(");
            const init = check("PUNCT", ";") ? null : parseForInit();
            expect("PUNCT", ";");
            const cond = check("PUNCT", ";") ? null : parseExpr();
            expect("PUNCT", ";");
            const update = check("PUNCT", ")") ? null : parseForUpdate();
            expect("PUNCT", ")");
            const body = parseStatementOrBlockAsList();
            return {kind: "For", init, cond, update, body, line: t.line};
        }

        if (t.type === "KEYWORD" && t.value === "return") {
            advance();
            const expr = check("PUNCT", ";") ? null : parseExpr();
            expect("PUNCT", ";");
            return {kind: "Return", expr, line: t.line};
        }

        if (t.type === "KEYWORD" && t.value === "break") {
            advance();
            expect("PUNCT", ";");
            return {kind: "Break", line: t.line};
        }

        if (t.type === "KEYWORD" && t.value === "continue") {
            advance();
            expect("PUNCT", ";");
            return {kind: "Continue", line: t.line};
        }

        if (isTypeAhead()) {
            return parseVarDecl();
        }

        const expr = parseExpr();
        expect("PUNCT", ";");
        return {kind: "ExprStmt", expr, line: t.line};
    }

    function parseForInit(): Stmt {
        const t = current();
        if (isTypeAhead()) return parseVarDecl(true);
        const expr = parseExpr();
        return {kind: "ExprStmt", expr, line: t.line};
    }

    function parseForUpdate(): Stmt {
        const t = current();
        const expr = parseExpr();
        return {kind: "ExprStmt", expr, line: t.line};
    }

    function parseVarDecl(noSemicolon = false): Stmt {
        const line = current().line;
        const varType = parseType();
        const name = expect("IDENT").value;
        let init: Expr | null = null;
        if (match("PUNCT", "=")) {
            init = parseExpr();
        }
        if (!noSemicolon) expect("PUNCT", ";");
        return {kind: "VarDecl", varType, name, init, line};
    }

    // Expression parsing, precedence climbing.
    function parseExpr(): Expr {
        return parseAssignment();
    }

    const ASSIGN_OPS = new Set(["=", "+=", "-=", "*=", "/=", "%="]);

    function parseAssignment(): Expr {
        const left = parseTernary();
        if (current().type === "PUNCT" && ASSIGN_OPS.has(current().value)) {
            const op = advance().value as Assign["op"];
            if (left.kind !== "Ident") {
                throw new ParseError("Invalid assignment target", left.line);
            }
            const expr = parseAssignment();
            return {kind: "Assign", op, name: left.name, expr, line: left.line};
        }
        return left;
    }

    function parseTernary(): Expr {
        const cond = parseLogicalOr();
        if (match("PUNCT", "?")) {
            const whenTrue = parseExpr();
            expect("PUNCT", ":");
            const whenFalse = parseExpr();
            return {kind: "Ternary", cond, whenTrue, whenFalse, line: cond.line};
        }
        return cond;
    }

    function parseLogicalOr(): Expr {
        let left = parseLogicalAnd();
        while (check("PUNCT", "||")) {
            const line = advance().line;
            const right = parseLogicalAnd();
            left = {kind: "Logical", op: "||", left, right, line};
        }
        return left;
    }

    function parseLogicalAnd(): Expr {
        let left = parseEquality();
        while (check("PUNCT", "&&")) {
            const line = advance().line;
            const right = parseEquality();
            left = {kind: "Logical", op: "&&", left, right, line};
        }
        return left;
    }

    function parseEquality(): Expr {
        let left = parseRelational();
        while (check("PUNCT", "==") || check("PUNCT", "!=")) {
            const op = advance().value as "==" | "!=";
            const right = parseRelational();
            left = {kind: "Binary", op, left, right, line: left.line};
        }
        return left;
    }

    function parseRelational(): Expr {
        let left = parseAdditive();
        while (
            check("PUNCT", "<") || check("PUNCT", "<=") ||
            check("PUNCT", ">") || check("PUNCT", ">=")
            ) {
            const op = advance().value as "<" | "<=" | ">" | ">=";
            const right = parseAdditive();
            left = {kind: "Binary", op, left, right, line: left.line};
        }
        return left;
    }

    function parseAdditive(): Expr {
        let left = parseMultiplicative();
        while (check("PUNCT", "+") || check("PUNCT", "-")) {
            const op = advance().value as "+" | "-";
            const right = parseMultiplicative();
            left = {kind: "Binary", op, left, right, line: left.line};
        }
        return left;
    }

    function parseMultiplicative(): Expr {
        let left = parseUnary();
        while (check("PUNCT", "*") || check("PUNCT", "/") || check("PUNCT", "%")) {
            const op = advance().value as "*" | "/" | "%";
            const right = parseUnary();
            left = {kind: "Binary", op, left, right, line: left.line};
        }
        return left;
    }

    function parseUnary(): Expr {
        const t = current();
        if (check("PUNCT", "-") || check("PUNCT", "!")) {
            const op = advance().value as "-" | "!";
            const expr = parseUnary();
            return {kind: "Unary", op, prefix: true, expr, line: t.line};
        }
        if (check("PUNCT", "++") || check("PUNCT", "--")) {
            const op = advance().value as "++" | "--";
            const expr = parseUnary();
            return {kind: "Unary", op, prefix: true, expr, line: t.line};
        }
        return parsePostfix();
    }

    function parsePostfix(): Expr {
        let expr = parsePrimary();
        while (true) {
            if (check("PUNCT", ".")) {
                advance();
                const method = expect("IDENT").value;
                expect("PUNCT", "(");
                const args: Expr[] = [];
                if (!check("PUNCT", ")")) {
                    do {
                        args.push(parseExpr());
                    } while (match("PUNCT", ","));
                }
                expect("PUNCT", ")");
                expr = {kind: "MethodCall", target: expr, method, args, line: expr.line};
                continue;
            }
            if (check("PUNCT", "++") || check("PUNCT", "--")) {
                const op = advance().value as "++" | "--";
                expr = {kind: "Unary", op, prefix: false, expr, line: expr.line};
                continue;
            }
            break;
        }
        return expr;
    }

    function parsePrimary(): Expr {
        const t = current();

        if (t.type === "INT_LITERAL") {
            advance();
            return {kind: "IntLiteral", value: Number(t.value), isLong: false, line: t.line};
        }
        if (t.type === "LONG_LITERAL") {
            advance();
            return {kind: "IntLiteral", value: Number(t.value), isLong: true, line: t.line};
        }
        if (t.type === "DOUBLE_LITERAL") {
            advance();
            return {kind: "DoubleLiteral", value: Number(t.value), line: t.line};
        }
        if (t.type === "STRING_LITERAL") {
            advance();
            return {kind: "StringLiteral", value: t.value, line: t.line};
        }
        if (t.type === "CHAR_LITERAL") {
            advance();
            return {kind: "CharLiteral", value: t.value, line: t.line};
        }
        if (t.type === "KEYWORD" && (t.value === "true" || t.value === "false")) {
            advance();
            return {kind: "BooleanLiteral", value: t.value === "true", line: t.line};
        }

        if (t.type === "PUNCT" && t.value === "(") {
            advance();
            const expr = parseExpr();
            expect("PUNCT", ")");
            return expr;
        }

        if (t.type === "KEYWORD" && t.value === "new") {
            advance();
            const className = expect("IDENT").value;
            if (className === "Scanner" && !scannerImported) {
                throw new ParseError(
                    `Cannot find class 'Scanner' - add 'import java.util.Scanner;' first`,
                    t.line
                );
            }
            expect("PUNCT", "(");
            const args: Expr[] = [];
            if (!check("PUNCT", ")")) {
                do {
                    args.push(parseExpr());
                } while (match("PUNCT", ","));
            }
            expect("PUNCT", ")");
            return {kind: "NewObject", className, args, line: t.line};
        }

        if (t.type === "IDENT" && t.value === "System" && peek(1).value === ".") {
            if (peek(2).value === "out") {
                advance();
                advance();
                advance();
                expect("PUNCT", ".");
                const variant = expect("IDENT").value;
                if (variant !== "println" && variant !== "print" && variant !== "printf") {
                    throw new ParseError(`Unsupported System.out.${variant}`, t.line);
                }
                expect("PUNCT", "(");
                const args: Expr[] = [];
                if (!check("PUNCT", ")")) {
                    do {
                        args.push(parseExpr());
                    } while (match("PUNCT", ","));
                }
                expect("PUNCT", ")");
                return {kind: "PrintCall", variant: variant as "println" | "print" | "printf", args, line: t.line};
            }
            if (peek(2).value === "in") {
                advance();
                advance();
                advance();
                return {kind: "SystemInRef", line: t.line};
            }
            throw new ParseError(`Unsupported reference 'System.${peek(2).value}'`, t.line);
        }

        if (t.type === "IDENT") {
            advance();
            if (check("PUNCT", "(")) {
                advance();
                const args: Expr[] = [];
                if (!check("PUNCT", ")")) {
                    do {
                        args.push(parseExpr());
                    } while (match("PUNCT", ","));
                }
                expect("PUNCT", ")");
                return {kind: "Call", callee: t.value, args, line: t.line};
            }
            return {kind: "Ident", name: t.value, line: t.line};
        }

        throw new ParseError(`Unexpected token '${t.value || t.type}'`, t.line);
    }

    return parseProgram();
}
